import { useEffect } from 'react';
import { apiKey, chatChannelName } from '../../networking/urlEndPoints';
import { getRecentChatsList } from '../../services/slices/chat/getRecentChatsList';
import { getCurrentShortTimeInAppTimezone } from '../../utils/constants/timezone';

import { getAblyRealtime } from '../../networking/ablyClient';
import { subscribeToDriverRealtimeEvent } from '../../services/realtime/realtimeSocket';

export const scrollToBottom = (messagesEndRef) => {
    if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
};

export const scrollToTop = (chatContainerRef) => {
    if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight; // Scroll to the top of the reversed column
    }
};

export const scrollToTopWithPadding = (chatContainerRef, padding = 0) => {
    if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight - padding; // Scroll to the top of the reversed column with padding
    }
};

export const handleViewportResize = (headerRef) => {
    const handleResize = () => {
        if (headerRef.current) {
            const viewportHeight = window.visualViewport.height;
            headerRef.current.style.top = `${window.visualViewport.offsetTop}px`;
            headerRef.current.style.height = `${Math.min(64, viewportHeight * 0.1)}px`;
        }
    };

    window.visualViewport.addEventListener('resize', handleResize);
    window.visualViewport.addEventListener('scroll', handleResize);

    return () => {
        window.visualViewport.removeEventListener('resize', handleResize);
        window.visualViewport.removeEventListener('scroll', handleResize);
    };
};

/**
 * Live messages inside an open chat.
 *
 * Listens on BOTH Ably and the Socket.IO server (the backend publishes to both, and
 * Ably is unreliable inside the Android WebView). Appending already de-dupes by
 * message_id, so a message arriving on both transports is harmless.
 *
 * Returns a { close } handle — NOT the Ably client. The client is shared app-wide, so
 * callers must never close it; close() just detaches this subscription.
 */
export const subscribeGroupMessages = (group_id, setChatAppendedMessages, setMyInputMessages, authPharmacistId, playMessageSound, pharmacyId) => {

    const sendChatMessages = `message-group-${group_id}`;

    const listener = async (message) => {
        if (message.name === sendChatMessages) {
            setChatAppendedMessages((prev) => {
                const messageExists = prev.some((msg) => msg.message_id === message.data.message_id);

                if (!messageExists) {
                    return [...prev, message?.data];
                }

                return prev;
            });
            setMyInputMessages([])
        }
    };

    let channel = null;
    const ably = apiKey ? getAblyRealtime() : null;
    if (ably) {
        channel = ably.channels.get(chatChannelName);
        channel.subscribe(sendChatMessages, listener);
    }

    // Parallel socket path. It delivers the raw payload, so shape it like an Ably
    // message to reuse the same listener.
    const socketSub = subscribeToDriverRealtimeEvent({
        pharmacyId,
        event: sendChatMessages,
        onEvent: (payload) => listener({ name: sendChatMessages, data: payload }),
    });

    return {
        close: () => {
            try { channel?.unsubscribe(sendChatMessages, listener); } catch (_) {}
            socketSub?.close();
        },
    };
}

/**
 * New-message notification banner.
 *
 * Listens on BOTH Ably and the Socket.IO server (backend publishes to both; Ably is
 * unreliable in the Android WebView, which is why this banner never appeared there).
 * Unlike group messages there is no natural de-dupe, so identical payloads arriving on
 * both transports are collapsed within a short window.
 *
 * Returns a { close } handle — NOT the Ably client (it is shared app-wide).
 */
export const SendMessageNotification = (pharmacyId, user_id, dispatch, location, setShowData, setShowNotification, playMessageSound) => {

    const sendChatMessages = `message-notification-${pharmacyId}-${user_id}`;

    const seen = new Map();
    const isDuplicate = (data) => {
        const key = [data?.message_id, data?.group_id, data?.title, data?.message].join('|');
        const now = Date.now();
        seen.forEach((at, k) => {
            if (now - at > 15000) seen.delete(k);
        });
        if (seen.has(key)) return true;
        seen.set(key, now);
        return false;
    };

    const listener = async (message) => {
        if (message.name === `message-notification-${pharmacyId}-${user_id}`) {
            if (isDuplicate(message?.data)) return;
            if (location?.pathname.startsWith('/initated-chat')) {
                // dispatch(getRecentChatsList())
            } else {
                // if (typeof playMessageSound === 'function') {
                //     playMessageSound()
                // }
                const params = { pharmacy_id: pharmacyId }

                if (pharmacyId) dispatch(getRecentChatsList(params))


                
                setShowData(message?.data)
                setShowNotification(true)
                setTimeout(() => {
                    setShowNotification(false)
                }, 5000)
            }

        }
    };

    let channel = null;
    const ably = apiKey ? getAblyRealtime() : null;
    if (ably) {
        channel = ably.channels.get('dp-common-chat-notification');
        channel.subscribe(sendChatMessages, listener);
    }

    // Parallel socket path (works on Android where Ably does not).
    const socketSub = subscribeToDriverRealtimeEvent({
        pharmacyId,
        event: sendChatMessages,
        onEvent: (payload) => listener({ name: sendChatMessages, data: payload }),
    });

    return {
        close: () => {
            try { channel?.unsubscribe(sendChatMessages, listener); } catch (_) {}
            socketSub?.close();
        },
    };
}


export const sendNotification = async (title, body, icon) => {
    if (!("Notification" in window)) {
        console.warn("This browser does not support desktop notification");
        return;
    }

    let permission = Notification.permission;

    if (permission === "default") {
        permission = await Notification.requestPermission();
    }

    if (permission === "granted") {
        const notification = new Notification(title, {
            body: body,
            icon: icon || 'https://your-default-icon-url.png',
            tag: 'react-notification'
        });

        notification.onclick = () => {
            window.focus();
            notification.close();
        };
    } else {
        console.warn("Notification permission denied");
    }
};

export const usePreventZoom = () => {
    useEffect(() => {
        // Function to disable pinch-to-zoom and double-tap zoom
        const preventZoom = (event) => {
            if (event?.touches?.length > 1 || event?.ctrlKey) {
                event.preventDefault();
            }
        };

        // Function to disable zoom on wheel scroll
        const preventWheelZoom = (event) => {
            if (event?.ctrlKey) {
                event.preventDefault();
            }
        };

        // Disable double-tap zoom
        document.addEventListener('touchstart', preventZoom, { passive: false });

        // Disable pinch-to-zoom
        document.addEventListener('gesturestart', preventZoom);

        // Disable zoom on wheel scroll
        document.addEventListener('wheel', preventWheelZoom, { passive: false });

        // Cleanup event listeners on component unmount
        return () => {
            document.removeEventListener('touchstart', preventZoom);
            document.removeEventListener('gesturestart', preventZoom);
            document.removeEventListener('wheel', preventWheelZoom);
        };
    }, []);
};


export const getCurrentTimeCanada = () => {
    return getCurrentShortTimeInAppTimezone();
}
